import { Drawer as DrawerPrimitive } from "@base-ui/react/drawer";
import { X } from "lucide-react";
import { cn } from "@ds/lib/utils";

/**
 * Drawer — the touch presentation of a sheet. Capture Sheet fields 12.11 and 8.6.
 *
 * A real bottom sheet, not a dialog parked at the bottom of the screen: the
 * captured system's sheets are draggable and the grab handle is a promise —
 * it says "you can flick this away". Drawing that bar without the behaviour
 * behind it is a defect, not a simplification.
 *
 * The drag is the primitive's. It publishes how far the sheet has been pulled
 * as `--drawer-swipe-movement-y`, which the panel follows one-to-one, and how
 * far through the dismissal the gesture is as `--drawer-swipe-progress`, which
 * fades the scrim in step. Release below the threshold and both spring back;
 * release past it and the sheet leaves. Focus trapping, Escape, scroll lock
 * and `aria-modal` come from the same primitive.
 */
const Drawer = DrawerPrimitive.Root;
const DrawerTrigger = DrawerPrimitive.Trigger;
const DrawerClose = DrawerPrimitive.Close;
const DrawerTitle = DrawerPrimitive.Title;
const DrawerDescription = DrawerPrimitive.Description;

function DrawerContent({
  className,
  children,
  closeLabel = "Close",
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Popup> & {
  /** Accessible name of the close control. */
  closeLabel?: string;
}) {
  return (
    <DrawerPrimitive.Portal>
      <DrawerPrimitive.Backdrop
        className={cn(
          "fixed inset-0 z-50 bg-scrim opacity-[calc(1-var(--drawer-swipe-progress))]",
          "transition-opacity duration-[var(--ds-duration-slow)] ease-out-ds",
          "data-swiping:duration-0 data-starting-style:opacity-0 data-ending-style:opacity-0",
        )}
      />
      <DrawerPrimitive.Viewport className="fixed inset-0 z-50 flex items-end justify-center">
        <DrawerPrimitive.Popup
          data-slot="drawer-content"
          className={cn(
            "relative flex max-h-[88svh] w-full max-w-lg flex-col",
            "rounded-t-[calc(var(--radius)+10px)] border border-b-0 border-border bg-background",
            "p-6 pb-8 outline-none",
            "[transform:translateY(var(--drawer-swipe-movement-y))]",
            "transition-transform duration-[var(--ds-duration-slow)] ease-out-ds",
            "data-starting-style:[transform:translateY(100%)] data-ending-style:[transform:translateY(100%)]",
            className,
          )}
          {...props}
        >
          {/* The grab handle. Drawn here, dragged by the popup itself. */}
          <div
            aria-hidden
            className="mx-auto mb-5 h-1 w-10 shrink-0 rounded-pill bg-muted-foreground/25"
          />
          <DrawerPrimitive.Close
            className={cn(
              "absolute top-6 right-6 rounded-pill p-1 text-foreground",
              "transition-colors duration-200 ease-out-ds hover:bg-muted",
              "focus-visible:outline-ring focus-visible:outline-2 focus-visible:outline-offset-2 outline-none",
            )}
          >
            <X className="size-5" />
            <span className="sr-only">{closeLabel}</span>
          </DrawerPrimitive.Close>
          <DrawerPrimitive.Content className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {children}
          </DrawerPrimitive.Content>
        </DrawerPrimitive.Popup>
      </DrawerPrimitive.Viewport>
    </DrawerPrimitive.Portal>
  );
}

export { Drawer, DrawerTrigger, DrawerClose, DrawerContent, DrawerTitle, DrawerDescription };
